import throttle from 'lodash/throttle'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

type Props = {
  elementRef?: React.RefObject<HTMLElement>
  topThreshold?: number
  bottomThreshold?: number
  throttleTime?: number
  isDisabled?: boolean
}

export const useScrollState = (props?: Props) => {
  const {
    elementRef,
    topThreshold = 0,
    bottomThreshold = 0,
    throttleTime = 200,
    isDisabled,
  } = props || {}
  const dispatch = useDispatch()
  const [canScrollBottom, setCanScrollBottom] = useState(true)
  const [canScrollTop, setCanScrollTop] = useState(false)

  useEffect(() => {
    const element = elementRef ? elementRef.current : document.scrollingElement
    const elementToListen = elementRef ? elementRef.current : window

    if (isDisabled || !element || !elementToListen) {
      return
    }

    const handleScroll = throttle(
      () => {
        setCanScrollBottom(
          element.scrollTop + element.clientHeight < element.scrollHeight - bottomThreshold,
        )
        setCanScrollTop(element.scrollTop > topThreshold)
      },
      throttleTime,
      { leading: true, trailing: true },
    )

    elementToListen.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)

    return () => {
      elementToListen.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [dispatch, elementRef, topThreshold, bottomThreshold, throttleTime, isDisabled])

  return {
    canScrollBottom,
    canScrollTop,
  }
}
